// ============================================
// Manor Miser - Sound Manager
// ============================================

// Placeholder sound system using Web Audio API
// Replace with actual sound files in Phase 4

class SoundManagerClass {
  private audioContext: AudioContext | null = null;
  private isMuted: boolean = false;
  private volume: number = 0.3;

  private getContext(): AudioContext | null {
    if (!this.audioContext) {
      try {
        this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      } catch (e) {
        console.warn('[Sound] Web Audio API not supported');
        return null;
      }
    }
    return this.audioContext;
  }

  private playTone(frequency: number, duration: number, type: OscillatorType = 'sine', fadeOut: boolean = true): void {
    if (this.isMuted) return;

    const ctx = this.getContext();
    if (!ctx) return;

    // Resume context if suspended (browser autoplay policy)
    if (ctx.state === 'suspended') {
      ctx.resume();
    }

    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, ctx.currentTime);

    gainNode.gain.setValueAtTime(this.volume, ctx.currentTime);
    
    if (fadeOut) {
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + duration);
    }

    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + duration);
  }

  private playChord(frequencies: number[], duration: number, type: OscillatorType = 'sine'): void {
    frequencies.forEach((freq, index) => {
      setTimeout(() => {
        this.playTone(freq, duration, type);
      }, index * 50);
    });
  }

  // -----------------------------
  // Sound Effects
  // -----------------------------

  /** Play when adding coins to bet */
  coinAdd(): void {
    this.playTone(880, 0.1, 'sine');
    setTimeout(() => this.playTone(1100, 0.1, 'sine'), 50);
  }

  /** Play when round starts */
  roundStart(): void {
    this.playChord([440, 554, 659], 0.3, 'triangle');
  }

  /** Play when timer is low (warning) */
  timerWarning(): void {
    this.playTone(440, 0.1, 'square');
  }

  /** Play when bets are revealed */
  reveal(): void {
    const notes = [523, 659, 784, 1047];
    notes.forEach((note, i) => {
      setTimeout(() => this.playTone(note, 0.2, 'sine'), i * 100);
    });
  }

  /** Play when a player is eliminated */
  elimination(): void {
    this.playTone(330, 0.15, 'sawtooth');
    setTimeout(() => this.playTone(262, 0.15, 'sawtooth'), 150);
    setTimeout(() => this.playTone(196, 0.3, 'sawtooth'), 300);
  }

  /** Play on victory */
  victory(): void {
    const fanfare = [523, 659, 784, 1047, 1319, 1568];
    fanfare.forEach((note, i) => {
      setTimeout(() => this.playTone(note, 0.3, 'sine'), i * 100);
    });
    // Final chord
    setTimeout(() => {
      this.playChord([1047, 1319, 1568], 0.5, 'triangle');
    }, 700);
  }

  /** Play on defeat/game over */
  defeat(): void {
    this.playTone(392, 0.3, 'triangle');
    setTimeout(() => this.playTone(330, 0.3, 'triangle'), 300);
    setTimeout(() => this.playTone(262, 0.5, 'triangle'), 600);
  }

  /** Button click sound */
  click(): void {
    this.playTone(600, 0.05, 'square');
  }

  /** Error/invalid action sound */
  error(): void {
    this.playTone(200, 0.1, 'sawtooth');
    setTimeout(() => this.playTone(180, 0.15, 'sawtooth'), 100);
  }

  /** Player joined notification */
  playerJoined(): void {
    this.playTone(660, 0.1, 'sine');
    setTimeout(() => this.playTone(880, 0.1, 'sine'), 100);
  }

  /** Player left notification */
  playerLeft(): void {
    this.playTone(440, 0.1, 'sine');
    setTimeout(() => this.playTone(330, 0.15, 'sine'), 100);
  }

  // -----------------------------
  // Controls
  // -----------------------------

  setMuted(muted: boolean): void {
    this.isMuted = muted;
  }

  toggleMute(): boolean {
    this.isMuted = !this.isMuted;
    return this.isMuted;
  }

  setVolume(volume: number): void {
    this.volume = Math.max(0, Math.min(1, volume));
  }

  get muted(): boolean {
    return this.isMuted;
  }

  /** Initialize audio context (call on user interaction) */
  init(): void {
    this.getContext();
  }
}

// Singleton instance
export const SoundManager = new SoundManagerClass();
